/**
 * Price utilities - matching admin app logic
 */

import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

const SHIPPING_FEES: Record<string, number> = {
    USD: 40,
    NGN: 56172,
};

const DEFAULT_SHIPPING_FEE = 46172;
const PERCENTAGE_AMOUNT_ON_FX = 0.08;

export interface ForexRates {
    [currency: string]: number;
}

export interface ForexData {
    rates?: ForexRates;
    placeholder?: boolean;
}

/**
 * Get shipping fee for a currency
 */
export const getShippingFee = (currency: string): number => {
    return SHIPPING_FEES[currency] ?? DEFAULT_SHIPPING_FEE;
};

/**
 * Calculates the price with shipping fees based on currency
 */
export const getPriceWithShipping = (price: number, currency: string): number => {
    const shippingFee = getShippingFee(currency);
    const finalPrice = parseFloat(price.toString());
    return finalPrice + shippingFee;
};

/**
 * Convert currency amount using forex rates
 */
export function convertCurrency(
    amount: number,
    fromCurrency: string,
    toCurrency: string,
    forexData: ForexData | null
): number {
    if (!forexData || !forexData.rates || fromCurrency === toCurrency || forexData.placeholder) {
        return amount;
    }

    const rates = forexData.rates;
    const amountWithMarkup = amount + amount * PERCENTAGE_AMOUNT_ON_FX;

    if (toCurrency === 'USD') {
        return amountWithMarkup / rates[fromCurrency];
    }

    if (fromCurrency === 'USD') {
        return amountWithMarkup * rates[toCurrency];
    }

    return amountWithMarkup * ((1 / rates[fromCurrency]) * rates[toCurrency]);
}

/**
 * Get forex rates from localStorage
 */
export function getForexRatesFromStorage(): ForexData | null {
    try {
        const forexStr = localStorage.getItem('forex');
        if (forexStr) {
            const forex = JSON.parse(forexStr);
            return {
                rates: forex.rates || null,
                placeholder: forex.placeholder || false
            };
        }
    } catch (error) {
        console.error('Error reading forex rates:', error);
    }
    return null;
}

/**
 * Fetch forex rates from Firestore and store in localStorage
 */
export async function fetchAndStoreForexRates(): Promise<void> {
    try {
        const forexDoc = await getDoc(doc(db, 'metadata', 'forex'));
        if (forexDoc.exists()) {
            const data = forexDoc.data();
            const rates = data?.rates || data;
            localStorage.setItem('forex', JSON.stringify({ rates, placeholder: false }));
        }
    } catch (error) {
        console.error('Error fetching forex rates:', error);
    }
}

/**
 * Get user's preferred currency from localStorage
 */
export function getUserPreferredCurrency(): string | null {
    try {
        const authStr = localStorage.getItem('auth');
        if (authStr) {
            const user = JSON.parse(authStr);
            return user.preferences?.currency || null;
        }
    } catch (error) {
        console.error('Error reading user preferences:', error);
    }
    return null;
}

/**
 * Format price with shipping and currency conversion (matching admin pipe)
 * Always displays in USD
 */
export function formatPriceWithShipping(price: number | string, sourceCurrency: string = 'USD'): string {
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    const currency = sourceCurrency || 'USD';
    const targetCurrency = 'USD'; // Always show in USD

    if (numPrice == null || isNaN(numPrice)) {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: targetCurrency,
        }).format(0);
    }

    // Step 1: Calculate price with shipping
    const priceWithShipping = getPriceWithShipping(numPrice, currency);

    // Step 2: Convert currency using forex rates (with 8% FX markup)
    const forexRates = getForexRatesFromStorage();
    const convertedPrice = convertCurrency(
        priceWithShipping,
        currency,
        targetCurrency,
        forexRates
    );

    // Step 3: Format and return
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: targetCurrency,
    }).format(convertedPrice);
}
